import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigProvider, theme as antdTheme, App } from 'antd';
import { COLOR_PRIMARY } from '../constants/styles';
import ipEmptyImage from '../assets/images/ip-empty.png';

export type ThemeMode = 'light' | 'dark';

export interface ThemeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  toggle: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useThemeMode = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeProvider');
  return ctx;
};

/**
 * 将 antd token 注入为全局 CSS 变量
 */
const GlobalCssVars: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = antdTheme.useToken();

  useEffect(() => {
    const root = document.documentElement;
    // 注入常用的 antd token 到 :root
    root.style.setProperty('--ant-color-primary', token.colorPrimary);
    root.style.setProperty('--ant-color-primary-hover', token.colorPrimaryHover);
    root.style.setProperty('--ant-color-bg-elevated', token.colorBgElevated);
    root.style.setProperty('--ant-color-bg-container', token.colorBgContainer);
    root.style.setProperty('--ant-color-text', token.colorText);
    root.style.setProperty('--ant-color-text-secondary', token.colorTextSecondary);
    root.style.setProperty('--ant-color-text-tertiary', token.colorTextTertiary);
    root.style.setProperty('--ant-color-text-quaternary', token.colorTextQuaternary);
    root.style.setProperty('--ant-color-border', token.colorBorder);
    root.style.setProperty('--ant-color-border-secondary', token.colorBorderSecondary);
    root.style.setProperty('--ant-color-fill-secondary', token.colorFillSecondary);
    root.style.setProperty('--ant-color-fill-tertiary', token.colorFillTertiary);
    root.style.setProperty('--ant-color-error', token.colorError);
    root.style.setProperty('--ant-color-error-bg', token.colorErrorBg);
    root.style.setProperty('--ant-color-white', '#ffffff');
  }, [token]);

  return <>{children}</>;
};

export interface ThemeProviderProps {
  children: React.ReactNode;
  /** Optional: customize primary color */
  primaryColor?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children,
  primaryColor = COLOR_PRIMARY,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('theme-mode') : null;
    return (saved === 'dark' || saved === 'light') ? (saved as ThemeMode) : 'light';
  });

  useEffect(() => {
    try { localStorage.setItem('theme-mode', mode); } catch {}
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const value = useMemo<ThemeContextValue>(() => ({
    mode,
    isDark: mode === 'dark',
    toggle: () => setModeState(prev => (prev === 'dark' ? 'light' : 'dark')),
    setMode: setModeState,
  }), [mode]);

  const algorithm = mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

  const token = useMemo(() => {
    const baseTokens = {
      colorPrimary: primaryColor,
    };
    
    if (mode === 'dark') {
      // 弱化分割线与描边颜色（影响 Divider、边框和我们用到的 CSS 变量 --ant-color-split）
      return {
        ...baseTokens,
        colorSplit: 'rgba(255,255,255,0.12)',
        colorBorderSecondary: 'rgba(255,255,255,0.18)',
      } as any;
    }
    return baseTokens as any;
  }, [mode, primaryColor]);

  // Component-level tokens: customize Layout Sider background
  const components = useMemo(() => ({
    Layout: {
      siderBg: mode === 'dark' ? '#121212ff' : '#ffffff',
    },
    Menu: mode === 'dark' ? {
    //   itemSelectedBg: 'rgba(255, 70, 70, 0.1)', 覆盖 antd 默认选中背景色
    } : {},
    Tag: {
      defaultBg: mode === 'dark' ? '#f77c5730' : '#f77c5728',
      defaultColor: '#ff8352ff',
    },
  }), [mode]);

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{ algorithm, token, components, cssVar: {}, hashed: false }}
        renderEmpty={() => (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <img src={ipEmptyImage} alt="empty" style={{ height: 200, marginBottom: 8 }} />
          </div>
        )}
      >
        <App style={{ minHeight: '100vh' }}>
          <GlobalCssVars>{children}</GlobalCssVars>
        </App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
